<template>
<header :class="['bg-white left-0 right-0 z-50 border-b border-gray-200 shadow-sm sticky top-0']">
  <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex justify-between items-center h-20">
      <router-link to="/" class="flex items-center space-x-3" aria-label="Home">
        <!-- CRITICAL: Preload logo for LCP -->
        <link rel="preload" :href="logo" as="image" type="image/svg+xml" fetchpriority="high">
        
        <!-- Optimized logo with proper dimensions -->
        <OptimizedImg
          :src="logo"
          alt="Online Self Test Logo"
          :imgClass="'h-10 sm:h-12 w-auto'"
          :width="360"
          :height="80"
          :eager="true"
          loading="eager"
          :disablePicture="true"
          fetchpriority="high"
          decoding="sync"
        />
       
      </router-link>

      <!-- Desktop Navigation with Icons - Load with low priority -->
      <div class="hidden lg:flex items-center space-x-8" data-priority="low">
        <router-link to="/learn" class="nav-link-with-icon" data-priority="low">
          <i class="fas fa-book-open"></i>
          <span>Learn</span>
        </router-link>
       
        <router-link to="/quiz" class="nav-link-with-icon" data-priority="low">
          <i class="fa-solid fa-trophy"></i>
          <span>Practice</span>
        </router-link>
        
        <router-link to="/community" class="nav-link-with-icon" data-priority="low">
          <i class="fas fa-users"></i>
          <span>Community</span>
        </router-link>
      </div>

      <!-- Desktop CTA - Defer non-critical content -->
      <div class="hidden lg:flex items-center space-x-3" data-priority="low">
        <div v-if="user" class="flex items-center space-x-4">
          <!-- Notification & Bookmark Controls - Defer -->
          <div class="relative flex items-center hidden space-x-4" data-priority="low">
            <button @click.prevent="goToBookmarks()" class="icon-btn hidden sm:inline-flex" title="Bookmarks" aria-label="Bookmarks" data-priority="low">
              <i class="fas fa-bookmark"></i>
            </button>

            <!-- Notification button -->
            <div class="relative inline-flex">
              <button 
                ref="notificationsBtn"
                @click="toggleNotifications" 
                class="icon-btn" 
                aria-label="Notifications"
                title="Notifications"
                data-priority="low"
              >
                <i class="fas fa-bell"></i>
                <span v-if="true" class="notification-badge">3</span>
              </button>

              <!-- Notifications Panel - Load on demand -->
              <div v-if="notificationsOpen" id="notificationsPanel" ref="notificationsPanel" :style="notificationsStyle" class="notification-panel slide-down z-[9999]">
                <div class="p-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 class="text-lg font-bold text-gray-900">Notifications</h3>
                  <button class="text-sm text-blue-600 font-semibold hover:text-blue-700">Mark all read</button>
                </div>
                <div class="max-h-80 overflow-y-auto">
                  <div class="notification-item">
                    <div class="notif-icon bg-blue-100 text-blue-600">
                      <i class="fas fa-clipboard-list"></i>
                    </div>
                    <div class="notif-body">
                      <p class="text-sm font-semibold text-gray-900">New Quiz Available</p>
                      <p class="text-xs text-gray-600 mt-1">Quadratic Equations - Due in 30 minutes</p>
                      <p class="text-xs text-gray-400 mt-1">5 minutes ago</p>
                    </div>
                  </div>

                  <div class="notification-item">
                    <div class="notif-icon bg-green-100 text-green-600">
                      <i class="fas fa-trophy"></i>
                    </div>
                    <div class="notif-body">
                      <p class="text-sm font-semibold text-gray-900">Achievement Unlocked!</p>
                      <p class="text-xs text-gray-600 mt-1">Speed Master badge earned +200 pts</p>
                      <p class="text-xs text-gray-400 mt-1">1 hour ago</p>
                    </div>
                  </div>

                  <div class="notification-item">
                    <div class="notif-icon bg-purple-100 text-purple-600">
                      <i class="fas fa-user-tie"></i>
                    </div>
                    <div class="notif-body">
                      <p class="text-sm font-semibold text-gray-900">Teacher's Feedback</p>
                      <p class="text-xs text-gray-600 mt-1">Mr. Kumar reviewed your Physics assignment</p>
                      <p class="text-xs text-gray-400 mt-1">2 hours ago</p>
                    </div>
                  </div>
                </div>
                <div class="p-3 text-center border-t border-gray-100">
                  <button class="text-sm text-blue-600 font-semibold hover:text-blue-700">View All Notifications</button>
                </div>
              </div>
            </div>
          </div>

          <!-- User Menu - Load on interaction -->
          <div class="relative">
            <button 
              aria-label="User menu" 
              ref="userMenuBtn" 
              @click="toggleUserMenu" 
              class="relative flex items-center space-x-3 text-sm px-3 py-2 transition-shadow min-w-[180px] user-menu-btn"
              data-priority="low"
            >
              <span v-if="userImage" class="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center">
                <!-- Lazy load user avatar -->
                <img 
                  :src="userImage" 
                  alt="avatar" 
                  class="w-9 h-9 rounded-full object-cover border-2 border-primary"
                  width="36"
                  height="36"
                  loading="lazy"
                  fetchpriority="low"
                  decoding="async"
                />
              </span>
              <span v-else class="w-9 h-9 rounded-full flex items-center justify-center bg-primary text-white font-bold text-sm border-2 border-primary">
                {{ userInitials }}
              </span>
              <div class="hidden md:block text-left">
                <div class="text-sm font-bold text-gray-900">{{ userFullName }}</div>
                <div class="text-xs text-gray-600">{{ userSubtitle }}</div>
              </div>
              <i class="fas fa-chevron-down ml-2 text-gray-500"></i>
            </button>

            <!-- User Menu Dropdown - Render only when needed -->
            <div v-if="userMenuOpen" id="userMenu" ref="userMenu" :style="userMenuStyle" class="notification-panel slide-down w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 z-[9999]">
              <!-- User Profile Section -->
              <div class="p-4 bg-gradient-to-br from-blue-600 to-purple-600 rounded-t-2xl text-white">
                <div class="flex items-center space-x-3">
                  <img 
                    v-if="userImage" 
                    :src="userImage" 
                    class="w-14 h-14 rounded-full border-3 border-white shadow-lg" 
                    @click="goToProfile" 
                    style="cursor:pointer;" 
                    width="56"
                    height="56"
                    loading="lazy"
                    alt="Profile"
                  >
                  <div v-else class="w-14 h-14 rounded-full border-3 border-white shadow-lg flex items-center justify-center bg-white/20 text-white font-bold text-lg">
                    {{ userInitials }}
                  </div>
                  <div class="flex-1">
                    <div class="font-bold text-lg">{{ userFullName }}</div>
                    <div class="text-sm text-blue-100">{{ userSubtitle }}</div>
                  </div>
                </div>
                <div class="mt-3 flex items-center justify-between bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 hidden">
                  <div>
                    <div class="text-xs text-blue-100">Total Points</div>
                    <div class="text-xl font-black">2,450</div>
                  </div>
                  <div class="text-2xl">🏆</div>
                </div>
              </div>

              <!-- Menu Items -->
              <div class="p-2">
                <a href="#" class="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors" @click.prevent="goToProfile">
                  <i class="fas fa-user text-blue-600 w-5"></i>
                  <span class="font-semibold text-gray-900" style="cursor:pointer;">My Profile</span>
                </a>
                           
                <a href="#" class="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors" @click.prevent="routeToDashboard()">
                  <i class="fas fa-tachometer-alt text-orange-600 w-5"></i>
                  <span class="font-semibold text-gray-900">Dashboard</span>
                </a>

                <!-- Bookmarks -->
                <a href="#" class="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors" @click.prevent="goToBookmarks()">
                  <i class="fas fa-bookmark text-indigo-600 w-5"></i>
                  <span class="font-semibold text-gray-900">Bookmarks</span>
                </a>

                <a href="#" class="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors" @click.prevent="goTo('/communities/my-group')">
                  <i class="fas fa-users text-indigo-600 w-5"></i>
                  <span class="font-semibold text-gray-900">My Group</span>
                </a>
                
                <a href="#" class="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors" @click.prevent="goTo('/goals')">
                  <i class="fas fa-bullseye text-indigo-600 w-5"></i>
                  <span class="font-semibold text-gray-900">My Goals</span>
                </a>
                
                <a href="#" class="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors" @click.prevent="goTo('/dashboard/settings')">
                  <i class="fas fa-cog text-gray-600 w-5"></i>
                  <span class="font-semibold text-gray-900">Settings</span>
                </a>
              </div>

              <div class="p-2 border-t border-gray-200">
                <button @click="handleLogout" aria-label="Logout" class="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-50 rounded-xl transition-colors text-red-600">
                  <i class="fas fa-sign-out-alt w-5"></i>
                  <span class="font-semibold">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="flex items-center space-x-3">
          <!-- Defer CTA buttons -->
          <button @click="openLogin" aria-label="Login" class="hidden sm:flex items-center px-4 py-2 border-2 border-blue-600 text-blue-600 rounded-lg font-bold text-sm hover:bg-blue-50 transition-all" data-priority="low">
            <i class="fas fa-sign-in-alt mr-2"></i>Login
          </button>
          <button @click="openTrial" aria-label="Sign Up Free" class="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-bold text-sm hover:shadow-lg transition-all" data-priority="low">
            <i class="fas fa-user-plus mr-2"></i>Sign Up Free
          </button>
        </div>
      </div>

      <!-- Mobile menu button -->
      <div class="lg:hidden flex items-center space-x-3">
        <template v-if="showExitControl">
          <button aria-label="Exit test" @click="$emit('exit-test')" class="flex items-center gap-2 px-3 py-2 bg-white border border-blue-100 text-blue-600 rounded-lg shadow-sm hover:bg-blue-50 transition-colors text-sm">
            <i class="fas fa-arrow-left"></i>
            <span>Return to Normal</span>
          </button>
        </template>
        <template v-else>
          <button aria-label="Start trial" v-if="!user" @click="openTrial" class="bg-primary text-white px-3 py-2 rounded-lg font-semibold hover:bg-secondary transition-colors text-sm">
            Trial
          </button>
          
          <!-- Hamburger Toggle -->
          <button 
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="hamburger-toggle"
            :class="{ 'active': mobileMenuOpen }"
            aria-label="Toggle menu"
            data-priority="low"
          >
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
            <span class="hamburger-line"></span>
          </button>
        </template>
      </div>
    </div>

    <!-- Mobile Menu - Render only when needed -->
    <div class="lg:hidden relative">
      <transition name="slide-down">
        <div v-if="mobileMenuOpen" class="mobile-menu-container">
          <div class="mobile-menu-content">
            <div class="mobile-menu-items">
              <!-- User Info Section -->
              <div v-if="user" class="user-info-section">
                <div class="flex items-center space-x-3">
                  <span v-if="userImage" class="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center">
                    <img 
                      :src="userImage" 
                      alt="avatar" 
                      class="w-10 h-10 rounded-full object-cover border-2 border-primary"
                      width="40"
                      height="40"
                      loading="lazy"
                    />
                  </span>
                  <span v-else class="w-10 h-10 rounded-full flex items-center justify-center bg-primary text-white font-bold text-sm border-2 border-primary">
                    {{ userInitials }}
                  </span>
                  <div class="flex-1">
                    <div class="text-sm font-bold text-gray-900">{{ userFullName }}</div>
                    <div class="text-xs text-gray-500">{{ user.email }}</div>
                    <div class="flex items-center mt-1">
                      <span class="text-xs text-gray-400">{{ userSubtitle }}</span>
                      <span class="w-2 h-2 rounded-full bg-emerald-400 border-2 border-white shadow status-pulse ml-2"></span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Navigation Items -->
              <router-link to="/learn" class="mobile-menu-item" @click="mobileMenuOpen = false">
                <div class="menu-item-icon">
                  <i class="fas fa-book-open"></i>
                </div>
                <div class="menu-item-content">
                  <span class="menu-item-title">Learn</span>
                  <span class="menu-item-description">Study materials</span>
                </div>
              </router-link>

              <router-link to="/quiz" class="mobile-menu-item" @click="mobileMenuOpen = false">
                <div class="menu-item-icon">
                  <i class="fa-solid fa-trophy"></i>
                </div>
                <div class="menu-item-content">
                  <span class="menu-item-title">Practice</span>
                  <span class="menu-item-description">Test your knowledge</span>
                </div>
              </router-link>

              <router-link to="/community" class="mobile-menu-item" @click="mobileMenuOpen = false">
                <div class="menu-item-icon">
                  <i class="fas fa-users"></i>
                </div>
                <div class="menu-item-content">
                  <span class="menu-item-title">Community</span>
                  <span class="menu-item-description">Collaborative learning</span>
                </div>
              </router-link>

              <template v-if="user">
                <button class="mobile-menu-item" @click="routeToDashboard(); mobileMenuOpen = false">
                  <div class="menu-item-icon">
                    <i class="fas fa-tachometer-alt"></i>
                  </div>
                  <div class="menu-item-content">
                    <span class="menu-item-title">Dashboard</span>
                    <span class="menu-item-description">Your learning overview</span>
                  </div>
                </button>

                <router-link to="/bookmarks" class="mobile-menu-item" @click="mobileMenuOpen = false">
                  <div class="menu-item-icon">
                    <i class="fas fa-bookmark"></i>
                  </div>
                  <div class="menu-item-content">
                    <span class="menu-item-title">Bookmarks</span>
                    <span class="menu-item-description">Saved resources</span>
                  </div>
                </router-link>

                <router-link to="/communities/my-group" class="mobile-menu-item" @click="mobileMenuOpen = false">
                  <div class="menu-item-icon">
                    <i class="fas fa-users"></i>
                  </div>
                  <div class="menu-item-content">
                    <span class="menu-item-title">My Group</span>
                    <span class="menu-item-description">Your groups</span>
                  </div>
                </router-link>

                <router-link to="/goals" class="mobile-menu-item" @click="mobileMenuOpen = false">
                  <div class="menu-item-icon">
                    <i class="fas fa-bullseye"></i>
                  </div>
                  <div class="menu-item-content">
                    <span class="menu-item-title">My Goals</span>
                    <span class="menu-item-description">Track your goals</span>
                  </div>
                </router-link>

                <button aria-label="View profile" @click.prevent="(user ? (goToProfile(), mobileMenuOpen = false) : openLogin())" class="mobile-menu-item">
                  <div class="menu-item-icon">
                    <i class="fas fa-user"></i>
                  </div>
                  <div class="menu-item-content">
                    <span class="menu-item-title">Profile</span>
                    <span class="menu-item-description">Manage your account</span>
                  </div>
                </button>

                <router-link to="/dashboard/settings" class="mobile-menu-item" @click="mobileMenuOpen = false">
                  <div class="menu-item-icon">
                    <i class="fas fa-cog"></i>
                  </div>
                  <div class="menu-item-content">
                    <span class="menu-item-title">Settings</span>
                    <span class="menu-item-description">Profile settings</span>
                  </div>
                </router-link>
                
                <button aria-label="Logout" @click="handleLogout" class="mobile-menu-item logout-item">
                  <div class="menu-item-icon">
                    <i class="fas fa-sign-out-alt"></i>
                  </div>
                  <div class="menu-item-content">
                    <span class="menu-item-title">Logout</span>
                    <span class="menu-item-description">Sign out of your account</span>
                  </div>
                </button>
              </template>

              <template v-else>
                <button aria-label="Sign in" @click="openLogin" class="mobile-menu-item">
                  <div class="menu-item-icon">
                    <i class="fas fa-sign-in-alt"></i>
                  </div>
                  <div class="menu-item-content">
                    <span class="menu-item-title">Sign In</span>
                    <span class="menu-item-description">Access your account</span>
                  </div>
                </button>
                
                <button aria-label="Start free trial" @click="openTrial" class="mobile-menu-cta">
                  <div class="cta-content">
                    <i class="fas fa-user-plus mr-2"></i>
                    <span>Start Free Trial</span>
                  </div>
                  <div class="cta-badge">NEW</div>
                </button>
              </template>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </nav>
</header>
</template>

<script>
import OptimizedImg from './OptimizedImg.vue';
import logo from '../assets/onlineselftest_logo.svg';
import { IMAGE_BASE } from '../services/api.js';

export default {
  name: "SiteHeader",
  components: {
    OptimizedImg
  },
  props: {
    showExitControl: {
      type: Boolean,
      default: false
    },
    isSticky: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      mobileMenuOpen: false,
      user: null,
      logo: logo,
      userMenuOpen: false,
      notificationsOpen: false,
      bookmarkActive: false,
      showAddRemarkModal: false,

      userMenuStyle: {
        position: 'fixed',
        top: '0px',
        left: 'auto',
        right: '16px'
      },
      notificationsStyle: {
        position: 'fixed',
        top: '0px',
        left: 'auto',
        right: '16px'
      },

      menuListenersAttached: false,
      resizeObserver: null
    }; 
  },
  computed: {
    userInitials() {
      if (!this.user || !this.user.name) return '';
      const names = this.user.name.trim().split(' ');
      if (names.length === 1) return names[0][0].toUpperCase();
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    },
    userImage() {
      if (!this.user) return null;
      if (this.user.profile_picture && !this.user.profile_picture.startsWith('http')) {
        return IMAGE_BASE + this.user.profile_picture.replace(/^\/+/, '');
      }
      return this.user.profile_picture || null;
    },
    userFullName() {
      return this.user && this.user.name ? this.user.name : '';
    },
    userGradeLevel() {
      if (!this.user) return '';
      const raw = this.user.user_type;
      const utNum = Number(raw);
      const isStudent = (Number.isFinite(utNum) && utNum === 1) || (typeof raw === 'string' && raw.toLowerCase() === 'student');
      if (!isStudent) return '';
      if (this.user.grade && this.user.grade.title) {
        return `Class ${this.user.grade.title}`;
      }
      if (this.user.grade_level_id) {
        return `Class ${this.user.grade_level_id}`;
      }
      return 'Student';
    },
    userSubtitle() {
      if (!this.user) return '';
      const parts = [];
      const raw = this.user.user_type;
      const utNum = Number(raw);
      const isStudent = (Number.isFinite(utNum) && utNum === 1) || (typeof raw === 'string' && raw.toLowerCase() === 'student');

      if (isStudent) {
        const grade = this.userGradeLevel;
        if (grade) parts.push(grade);
        const role = this.userTypeText;
        if (role) parts.push(role);
      } else {
        const role = this.userTypeText;
        if (role) parts.push(role);
      }

      return parts.join(' • ');
    },
    userTypeText() {
      if (!this.user) return '';
      const raw = this.user.user_type;
      const numeric = Number(raw);
      const mapping = {
        1: 'Student',
        2: 'Teacher',
        3: 'Parent',
        4: 'Institution',
        5: 'Professional'
      };
      if (Number.isFinite(numeric) && mapping[numeric]) return mapping[numeric];

      if (typeof raw === 'string' && raw.trim().length > 0) {
        const normalized = raw.trim().toLowerCase();
        const textMap = {
          'student': 'Student',
          'teacher': 'Teacher',
          'parent': 'Parent',
          'institution': 'Institution',
          'professional': 'Professional'
        };
        return textMap[normalized] || (raw.charAt(0).toUpperCase() + raw.slice(1));
      }

      return '';
    }
  },
  mounted() {
    // Defer user data loading to reduce main thread blocking
    setTimeout(() => {
      this.loadUserData();
    }, 100);
    
    this._boundLoadUserData = () => this.loadUserData();
    window.addEventListener('storage', this._boundLoadUserData);
    window.addEventListener('user:updated', this._boundLoadUserData);
    
    // Use requestIdleCallback for non-critical event listeners
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        document.addEventListener('click', this.handleClickOutside);
      });
    } else {
      setTimeout(() => {
        document.addEventListener('click', this.handleClickOutside);
      }, 200);
    }
    
    this.setupResizeObserver();
  },
  beforeUnmount() {
    if (this._boundLoadUserData) {
      window.removeEventListener('storage', this._boundLoadUserData);
      window.removeEventListener('user:updated', this._boundLoadUserData);
      delete this._boundLoadUserData;
    }
    document.removeEventListener('click', this.handleClickOutside);

    this.cleanupEventListeners();
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
  methods: {
    goToProfile() {
      if (this.user && this.user.user_name) {
        this.goTo(`/profile/${this.user.user_name}`);
      } else {
        this.goTo('/profile');
      }
    },
    goToBookmarks() {
      // Debounce to reduce TBT
      if (this._goToBookmarksTimeout) {
        clearTimeout(this._goToBookmarksTimeout);
      }
      
      this._goToBookmarksTimeout = setTimeout(() => {
        const authToken = this.getCookie('authToken');
        console.debug('[SiteHeader] goToBookmarks authToken present:', !!authToken);
        if (!authToken) {
          try {
            if (typeof window !== 'undefined' && typeof window.openLoginModal === 'function') {
              window.openLoginModal();
            }
          } catch (e) {
            console.debug('goToBookmarks: openLoginModal error', e);
          }
          this.$emit('open-login');
          return;
        }

        try {
          if (this.$router && typeof this.$router.push === 'function') {
            this.$router.push({ name: 'Bookmarks' }).catch(err => {
              console.warn('[SiteHeader] router.push by name failed:', err);
              try {
                this.$router.push('/bookmarks').catch(e2 => {
                  console.warn('[SiteHeader] router.push by path failed:', e2);
                  window.location.href = '/bookmarks';
                });
              } catch (e3) {
                console.warn('[SiteHeader] router.push by path threw:', e3);
                window.location.href = '/bookmarks';
              }
            });
          } else {
            window.location.href = '/bookmarks';
          }
        } catch (err) {
          console.error('[SiteHeader] goToBookmarks final redirect error:', err);
          window.location.href = '/bookmarks';
        }
      }, 50);
    },
    handleClickOutside(event) {
      const header = this.$el;
      const isClickInsideHeader = header.contains(event.target);
      const clickedInsideUserMenu = this.$refs.userMenu && this.$refs.userMenu.contains(event.target);
      const clickedInsideNotifications = this.$refs.notificationsPanel && this.$refs.notificationsPanel.contains(event.target);

      if (!isClickInsideHeader && this.mobileMenuOpen) {
        this.mobileMenuOpen = false;
      }

      if (!isClickInsideHeader && !clickedInsideUserMenu) {
        this.userMenuOpen = false;
      }
      if (!isClickInsideHeader && !clickedInsideNotifications) {
        this.notificationsOpen = false;
      }
    },
    toggleUserMenu() {
      // Debounce to reduce TBT
      if (this._toggleUserMenuTimeout) {
        clearTimeout(this._toggleUserMenuTimeout);
      }
      
      this._toggleUserMenuTimeout = setTimeout(() => {
        this.userMenuOpen = !this.userMenuOpen;
        this.notificationsOpen = false;

        if (this.userMenuOpen) {
          this.$nextTick(() => {
            this.positionUserMenu();
            this.attachEventListeners();
          });
        } else {
          this.cleanupEventListeners();
        }
      }, 50);
    },
    toggleNotifications() {
      // Debounce to reduce TBT
      if (this._toggleNotificationsTimeout) {
        clearTimeout(this._toggleNotificationsTimeout);
      }
      
      this._toggleNotificationsTimeout = setTimeout(() => {
        this.notificationsOpen = !this.notificationsOpen;
        this.userMenuOpen = false;

        if (this.notificationsOpen) {
          this.$nextTick(() => {
            this.positionNotificationsPanel();
            this.attachEventListeners();
          });
        } else {
          this.cleanupEventListeners();
        }
      }, 50);
    },
    openLogin() {
      // Debounce to reduce TBT
      if (this._openLoginTimeout) {
        clearTimeout(this._openLoginTimeout);
      }
      
      this._openLoginTimeout = setTimeout(() => {
        this.mobileMenuOpen = false;
        try {
          if (typeof window !== 'undefined' && typeof window.openLoginModal === 'function') {
            window.openLoginModal();
          }
        } catch (e) {
          console.debug('openLogin modal trigger error:', e);
        }
        this.$emit('open-login');
      }, 50);
    },
    openTrial() {
      // Debounce to reduce TBT
      if (this._openTrialTimeout) {
        clearTimeout(this._openTrialTimeout);
      }
      
      this._openTrialTimeout = setTimeout(() => {
        this.mobileMenuOpen = false;
        try {
          if (typeof window !== 'undefined' && typeof window.openTrialModal === 'function') {
            window.openTrialModal();
          }
        } catch (e) {
          console.debug('openTrial modal trigger error:', e);
        }
        this.$emit('open-trial');
      }, 50);
    },
    openAddRemark() {
      this.mobileMenuOpen = false;
      this.userMenuOpen = false;
      try {
        if (typeof window !== 'undefined' && typeof window.openAddRemarkModal === 'function') {
          window.openAddRemarkModal();
        }
      } catch (e) {
        console.debug('openAddRemark modal trigger error:', e);
      }
      this.$emit('open-add-remark');
    },
    openStudyGoals() {
      this.mobileMenuOpen = false;
      this.userMenuOpen = false;
      this.notificationsOpen = false;
      try {
        if (typeof window !== 'undefined' && typeof window.openStudyGoalsModal === 'function') {
          window.openStudyGoalsModal();
        }
      } catch (e) {
        console.debug('openStudyGoals modal trigger error:', e);
      }
      this.$emit('open-study-goals');
    },
    loadUserData() {
      try {
        const raw = localStorage.getItem('user');
        if (raw) {
          this.user = JSON.parse(raw);
        } else {
          const userCookie = this.getCookie('user');
          if (userCookie) {
            try {
              this.user = JSON.parse(decodeURIComponent(userCookie));
            } catch (e) {
              console.error('Error parsing user cookie:', e);
              this.user = null;
            }
          } else {
            this.user = null;
          }
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
        this.user = null;
      }
    },
    positionUserMenu() {
      try {
        const btn = this.$refs.userMenuBtn;
        const menu = this.$refs.userMenu;
        if (!btn || !menu) return;

        const rect = btn.getBoundingClientRect();
        const menuWidth = menu.offsetWidth || 320;
        const spacing = 8;
        
        let left = rect.right - menuWidth;
        const padding = 12;
        if (left < padding) left = padding;
        if (left + menuWidth > window.innerWidth - padding) left = window.innerWidth - padding - menuWidth;

        const top = Math.max(padding, rect.bottom + spacing);

        this.userMenuStyle = {
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`
        }; 
      } catch (e) {
        console.error('positionUserMenu error', e);
      }
    },
    positionNotificationsPanel() {
      try {
        const btn = this.$refs.notificationsBtn;
        const panel = this.$refs.notificationsPanel;
        if (!btn || !panel) return;

        const rect = btn.getBoundingClientRect();
        const panelWidth = panel.offsetWidth || 320;
        const spacing = 8;
        const padding = 12;

        let left = rect.right - panelWidth;
        if (left < padding) left = padding;
        if (left + panelWidth > window.innerWidth - padding) left = window.innerWidth - padding - panelWidth;

        const top = Math.max(padding, rect.bottom + spacing);
        this.notificationsStyle = {
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`
        };
      } catch (e) {
        console.error('positionNotificationsPanel error', e);
      }
    },
    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    },
    handleLogout() {
      // Debounce to reduce TBT
      if (this._handleLogoutTimeout) {
        clearTimeout(this._handleLogoutTimeout);
      }
      
      this._handleLogoutTimeout = setTimeout(() => {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
        
        const cookies = ['user', 'authToken', 'token'];
        cookies.forEach(name => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; secure; samesite=strict`;
        });

        this.user = null;
        this.userMenuOpen = false;
        this.notificationsOpen = false;
        this.mobileMenuOpen = false;

        this.$emit('logged-out');

        if (this.$toast) {
          this.$toast.success('Successfully logged out');
        } else if (this.$showToast) {
          this.$showToast.success('Successfully logged out');
        }

        if (this.$router) {
          this.$router.push('/');
        } else {
          window.location.href = '/';
        }
        
        window.location.reload();
      }, 50);
    },
    routeToDashboard() {
      // Debounce to reduce TBT
      if (this._routeToDashboardTimeout) {
        clearTimeout(this._routeToDashboardTimeout);
      }
      
      this._routeToDashboardTimeout = setTimeout(() => {
        this.userMenuOpen = false;
        this.notificationsOpen = false;
        this.mobileMenuOpen = false;

        let target = '/dashboard';
        const user = this.user;

        if (user && user.user_type) {
          const numeric = Number(user.user_type);
          if (Number.isFinite(numeric)) {
            if (numeric === 2) target = '/control-panel/teacher';
            else if (numeric === 3) target = '/control-panel/parent';
            else if (numeric === 4) target = '/control-panel/institute';
            else target = '/dashboard';
          } else if (typeof user.user_type === 'string') {
            const t = user.user_type.trim().toLowerCase();
            if (t === 'teacher') target = '/control-panel/teacher';
            else if (t === 'parent') target = '/control-panel/parent';
            else if (t === 'institution' || t === 'institute') target = '/control-panel/institute';
            else target = '/dashboard';
          }
        }

        if (this.$router && typeof this.$router.push === 'function') {
          this.$router.push(target);
        } else {
          window.location.href = target;
        }
      }, 50);
    },
    goTo(path) {
      this.userMenuOpen = false;
      this.notificationsOpen = false;
      this.mobileMenuOpen = false;
      if (this.$router) this.$router.push(path);
    },
    attachEventListeners() {
      if (!this.menuListenersAttached) {
        window.addEventListener('resize', this.positionUserMenu);
        window.addEventListener('scroll', this.positionUserMenu, true);
        this.menuListenersAttached = true;
      }
    },
    cleanupEventListeners() {
      if (this.menuListenersAttached) {
        window.removeEventListener('resize', this.positionUserMenu);
        window.removeEventListener('scroll', this.positionUserMenu, true);
        this.menuListenersAttached = false;
      }
    },
    setupResizeObserver() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.userMenuOpen) this.positionUserMenu();
          if (this.notificationsOpen) this.positionNotificationsPanel();
        });
        
        this.resizeObserver.observe(document.body);
      }
    }
  }
};
</script>

<style scoped>
/* Navigation Links with Icons */
.nav-link-with-icon {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #4b5563;
  font-weight: 500;
  transition: all 0.2s ease;
  padding: 6px 12px;
  border-radius: 8px;
  text-decoration: none;
}

.nav-link-with-icon i {
  font-size: 16px;
  transition: transform 0.2s ease;
}

.nav-link-with-icon:hover {
  color: #9233ea;
}

.nav-link-with-icon:hover i {
  transform: scale(1.1);
}

.nav-link-with-icon.router-link-active {
  color: #9233ea;
  font-weight: 600;
}

.nav-link-with-icon.router-link-active i {
  color: #9233ea;
}

/* Professional Hamburger Toggle */
.hamburger-toggle {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  position: relative;
  transition: all 0.3s ease;
}

.hamburger-line {
  display: block;
  height: 2px;
  width: 100%;
  background-color: #6b7280;
  border-radius: 1px;
  transition: all 0.3s ease;
  transform-origin: center;
}

.hamburger-toggle:hover .hamburger-line {
  background-color: #4b5563;
}

.hamburger-toggle.active .hamburger-line:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
  background-color: #EC7263;
}

.hamburger-toggle.active .hamburger-line:nth-child(2) {
  opacity: 0;
  transform: translateX(8px);
}

.hamburger-toggle.active .hamburger-line:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
  background-color: #EC7263;
}

/* Mobile Menu Container */
.mobile-menu-container {
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  max-width: 90vw;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  margin-top: 8px;
  z-index: 50;
  overflow: hidden;
  max-height: 70vh;
  overflow-y: auto;
}

/* Slide down animation */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  transform-origin: top;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: scaleY(0.8) translateY(-10px);
}

.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  transform: scaleY(1) translateY(0);
}

/* Mobile Menu Content */
.mobile-menu-content {
  padding: 0;
}

.mobile-menu-items {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* User Info Section */
.user-info-section {
  padding: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  margin-bottom: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* Menu Items */
.mobile-menu-item {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  color: #4b5563;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  border: none;
  background: transparent;
  text-align: left;
  width: 100%;
  gap: 12px;
}

.mobile-menu-item:hover {
  background: #f3f4f6;
  color: #1f2937;
  transform: translateX(2px);
}

.mobile-menu-item.router-link-active {
  background: #eff6ff;
  color: #9233ea;
  box-shadow: inset 2px 0 0 #9233ea;
}

.menu-item-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: color 0.2s ease;
  flex-shrink: 0;
  margin-top: 1px;
}

.mobile-menu-item:hover .menu-item-icon,
.mobile-menu-item.router-link-active .menu-item-icon {
  color: inherit;
}

.menu-item-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.menu-item-title {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
}

.menu-item-description {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.2;
}

/* Logout Item */
.logout-item {
  color: #dc2626;
  margin-top: 4px;
}

.logout-item:hover {
  background: #fef2f2;
  color: #dc2626;
  transform: translateX(2px);
}

.logout-item .menu-item-icon {
  color: #dc2626;
}

/* CTA Button */
.mobile-menu-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #9233ea 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 8px;
  margin-top: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.mobile-menu-cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
}

.cta-content {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
}

.cta-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* Online status pulse */
.status-pulse {
  transform: scale(1);
  animation: status-pulse 1.8s ease-in-out infinite;
}

@keyframes status-pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.35); opacity: 0.65; }
  100% { transform: scale(1); opacity: 1; }
}

/* Icon button styling for header */
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  color: #6b7280;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.08s ease;
  position: relative;
}

.icon-btn i {
  font-size: 16px;
}

.icon-btn:hover {
  color: #1f2937;
}

/* Active icon state */
.icon-btn.active-icon,
.icon-btn.active-icon i {
  color: #9233ea;
}

.notification-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ef4444;
  color: white;
  font-size: 11px;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}

.notification-panel {
  position: absolute;
  right: 0;
  margin-top: 8px;
  width: 360px;
  max-width: 92vw;
  background: white;
  border: 1px solid #e6e9ee;
  box-shadow: 0 18px 30px rgba(17,24,39,0.08);
  overflow: hidden;
  z-index: 60;
}

.notification-item {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  align-items: flex-start;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.12s ease, transform 0.08s ease;
}

.notification-item:hover {
  background: #f8fafc;
  transform: translateX(2px);
}

.notif-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.notif-body {
  flex: 1;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .nav-link-with-icon span {
    display: none;
  }
  
  .nav-link-with-icon {
    padding: 6px;
  }
}

/* Prevent layout shifts by reserving space for images */
img[width][height] {
  aspect-ratio: attr(width) / attr(height);
}

/* Screen reader only class */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Optimize animations for performance */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  
  .status-pulse {
    animation: none;
  }
  
  .mobile-menu-item:hover,
  .notification-item:hover,
  .mobile-menu-cta:hover {
    transform: none;
  }
}

/* Optimize for mobile performance */
@media (max-width: 768px) {
  .notification-panel,
  .mobile-menu-container {
    transform: translateZ(0);
    backface-visibility: hidden;
    will-change: transform, opacity;
  }
}
</style>